/**
 * Employment verification service
 * Handles employment verification workflow
 */

import axios from "axios";
import api from "../api";

export interface EmploymentVerificationStatus {
  status: "pending" | "verified";
  verified_at?: string;
}

export interface VerificationSubmitData {
  company_name?: string;
  company_domain?: string;
  work_email: string;
  handle_id?: string;
}

export interface VerificationConfirmData {
  verification_code: string;
  handle_id?: string;
  attempt_id?: string;
}

export interface VerificationResendData {
  work_email: string;
  handle_id?: string;
  attempt_id?: string;
}

/**
 * Check if user is verified
 */
export async function isUserVerified() {
  try {
    const response = await api.get("/api/account/verified/");
    return response.data as { verified: boolean };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Get employment verification status
 */
export async function getEmploymentVerificationStatus() {
  try {
    const response = await api.get("/api/account/employment-verification/status");
    return response.data as EmploymentVerificationStatus;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { detail?: string } | undefined;
      throw new Error(data?.detail || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Submit employment verification request
 */
export async function submitEmploymentVerification(data: VerificationSubmitData) {
  try {
    const response = await api.post("/api/account/employment-verification/submit", data);
    return response.data as { status: string; attempt_id?: string; expires_at?: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { error?: string; detail?: string } | undefined;

      const messageMap: Record<string, string> = {
        invalid_work_email: "Enter a valid work email.",
        domain_mismatch: "Work email must match the company domain.",
      };

      const code = body?.error;
      if (code && messageMap[code]) {
        throw new Error(messageMap[code]);
      }

      throw new Error(body?.detail || code || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Confirm employment verification with code
 */
export async function confirmEmploymentVerification(data: VerificationConfirmData) {
  try {
    const response = await api.post("/api/account/employment-verification/confirm", data);
    return response.data as { status: string; verified_at?: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { error?: string; detail?: string } | undefined;

      const messageMap: Record<string, string> = {
        expired_code: "expired_code",
        invalid_code: "invalid_code",
      };

      const code = body?.error;
      if (code && messageMap[code]) {
        throw new Error(messageMap[code]);
      }

      throw new Error(body?.detail || code || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Resend employment verification code
 */
export async function resendEmploymentVerification(data: VerificationResendData) {
  try {
    const response = await api.post("/api/account/employment-verification/resend", data);

    const headers = response.headers as Record<string, string | undefined>;
    const retryAfterHeader = headers?.["retry-after"] ?? headers?.["Retry-After"];
    const retryAfter = retryAfterHeader ? Number.parseInt(retryAfterHeader, 10) : undefined;

    return {
      ...response.data,
      retry_after: typeof retryAfter === "number" && !Number.isNaN(retryAfter) ? retryAfter : undefined,
    } as { status: string; expires_at?: string; cooldown?: number; retry_after?: number };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data as { error?: string; detail?: string } | undefined;

      const messageMap: Record<string, string> = {
        work_email_required: "Provide the work email used for verification.",
        no_pending_verification: "No pending verification found to resend.",
      };

      const code = body?.error;
      if (code && messageMap[code]) {
        throw new Error(messageMap[code]);
      }

      throw new Error(body?.detail || code || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Request email verification
 */
export async function requestVerificationEmail(data: { work_email: string }) {
  try {
    const response = await api.post("/api/account/request/", data);
    return response.data as { detail: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}

/**
 * Verify email with token
 */
export async function verifyEmail(data: { token: string | null }) {
  try {
    if (!data.token) throw new Error("No verification token provided.");

    const response = await api.get(`/api/account/confirm/`, {
      params: { t: data.token },
    });

    return response.data as { detail: string };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.detail || error.message);
    }
    throw new Error("An unknown error occured!");
  }
}
